/**
 * Fonts, fetched when something needs them.
 *
 * The page used to ask Google for all twenty-odd faces up front — every name
 * font and skin face — on every visit, though a first visit only ever shows two.
 * Now only Bungee and Monoton come with the page; the rest arrive the first time
 * a skin or a name on screen calls for them.
 */

/** Each face's Google Fonts query, with the weight it is drawn at. */
const GOOGLE: Record<string, string> = {
  'Press Start 2P': 'Press+Start+2P',
  VT323: 'VT323',
  'Special Elite': 'Special+Elite',
  Cinzel: 'Cinzel:wght@600',
  Caveat: 'Caveat:wght@600',
  Bangers: 'Bangers',
  Pacifico: 'Pacifico',
  Creepster: 'Creepster',
  Nosifer: 'Nosifer',
  'Mountains of Christmas': 'Mountains+of+Christmas:wght@700',
  'Dancing Script': 'Dancing+Script:wght@600',
  'Bungee Shade': 'Bungee+Shade',
  Fredoka: 'Fredoka:wght@500',
  'Permanent Marker': 'Permanent+Marker',
  Orbitron: 'Orbitron:wght@600',
  Rye: 'Rye',
  Righteous: 'Righteous',
  'Playfair Display': 'Playfair+Display:ital,wght@1,600',
  'Rubik Bubbles': 'Rubik+Bubbles',
  UnifrakturMaguntia: 'UnifrakturMaguntia',
};

/** The faces a skin sets its signs and logo in. */
const SKIN_FONTS: Record<string, string[]> = {
  arcade: ['Press Start 2P'],
  terminal: ['VT323'],
  typewriter: ['Special Elite'],
  archive: ['Cinzel'],
  yearbook: ['Cinzel'],
  museum: ['Cinzel'],
  royal: ['Cinzel'],
  chalkboard: ['Caveat'],
  tabloid: ['Bangers'],
};

const requested = new Set<string>();

/** Asks for one face, once. Unknown or built-in faces are ignored. */
export function ensureFont(family: string): void {
  if (typeof document === 'undefined') return;
  const query = GOOGLE[family];
  if (!query || requested.has(family)) return;
  requested.add(family);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${query}&display=swap`;
  document.head.appendChild(link);
}

/** The first face in a CSS font stack: `'Special Elite', ui-monospace` → `Special Elite`. */
export function firstFamily(stack: string): string {
  return (stack.split(',')[0] ?? '').trim().replace(/^['"]|['"]$/g, '');
}

/** Asks for whatever a skin draws its signs in. */
export function ensureSkinFonts(skin: string | null | undefined): void {
  for (const family of SKIN_FONTS[skin ?? ''] ?? []) ensureFont(family);
}
